import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useParams } from 'react-router-dom'
import { brandService as sharedBrandService } from '../../../shared/brand/services/brandService'
import { findBrandDetails } from '../mappers/brandDetailsMapper'
import { brandDocumentService as sharedDocumentService } from '../services/brandDocumentService'
import { brandExtractionService as sharedExtractionService } from '../services/brandExtractionService'

const BrandDetailsContext = createContext(null)

export const BrandDetailsProvider = ({
  brandService: service,
  children,
  documentService: providedDocumentService,
  extractionService: providedExtractionService,
}) => {
  const { brandSlug = '' } = useParams()
  const brandService = service || sharedBrandService
  const documentService = providedDocumentService || sharedDocumentService
  const extractionService = providedExtractionService || sharedExtractionService
  const [brand, setBrand] = useState(null)
  const [documents, setDocuments] = useState([])
  const [documentsError, setDocumentsError] = useState(null)
  const [error, setError] = useState(null)
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const loadDocuments = useCallback(
    async (selectedBrand) => {
      if (!selectedBrand) return []
      setDocumentsError(null)
      setIsDocumentsLoading(true)
      try {
        const response = await documentService.getBrandDocuments(selectedBrand)
        setDocuments(response)
        return response
      } catch (requestError) {
        setDocumentsError(requestError)
        throw requestError
      } finally {
        setIsDocumentsLoading(false)
      }
    },
    [documentService],
  )

  const loadBrand = useCallback(async () => {
    setError(null)
    setIsLoading(true)
    try {
      const response = await brandService.getAllBrands()
      const matchedBrand = findBrandDetails(response, brandSlug)
      if (!matchedBrand) throw new Error('Brand details could not be found.')
      // The list only identifies the brand; its AssetHub project and statuses
      // come from /details.
      const detailedBrand = {
        ...matchedBrand,
        ...(await extractionService.getDetails(matchedBrand)),
      }
      setBrand(detailedBrand)
      await loadDocuments(detailedBrand).catch(() => {})
      return detailedBrand
    } catch (requestError) {
      setBrand(null)
      setError(requestError)
      throw requestError
    } finally {
      setIsLoading(false)
    }
  }, [brandService, brandSlug, extractionService, loadDocuments])

  const deleteDocument = useCallback(
    async (documentId) => {
      await documentService.deleteDocument(documentId)
      await loadDocuments(brand)
    },
    [brand, documentService, loadDocuments],
  )

  useEffect(() => {
    loadBrand().catch(() => {})
  }, [loadBrand])

  const value = useMemo(
    () => ({
      brand,
      brandSlug,
      deleteDocument,
      documents,
      documentsError,
      error,
      isDocumentsLoading,
      isLoading,
      refreshBrand: loadBrand,
      refreshDocuments: () => loadDocuments(brand),
    }),
    [
      brand,
      brandSlug,
      deleteDocument,
      documents,
      documentsError,
      error,
      isDocumentsLoading,
      isLoading,
      loadBrand,
      loadDocuments,
    ],
  )

  return (
    <BrandDetailsContext.Provider value={value}>
      {children}
    </BrandDetailsContext.Provider>
  )
}

export const useBrandDetails = () => {
  const context = useContext(BrandDetailsContext)
  if (!context) {
    throw new Error('useBrandDetails must be used within BrandDetailsProvider')
  }
  return context
}
